import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import useIsClient from '@/hooks/useIsClient';
import styles from './index.module.scss';

// PAG wasm 资源地址，统一走业务 CDN。
const PAG_WASM_URL = 'https://roboneo-public.meitudata.com/public/6a72fadb767716661lV0zw39DF2541.wasm';
// 官方 PAG 预览页在 Firefox 下使用 ffavc 软件解码器，避免 BMP/video sequence 停在首帧。
const FFAVC_SCRIPT_URL = 'https://roboneo-public.meitudata.com/public/6a7c32a840240395265r0nDiuh3447.js';
// ffavc 默认按脚本同目录查找 ffavc.wasm；这里显式指定业务 CDN 资源。
const FFAVC_WASM_URL = 'https://roboneo-public.meitudata.com/public/6a7c3e28131712481uP9j6rT2T3419.wasm';

type PAGModule = Awaited<ReturnType<(typeof import('libpag'))['PAGInit']>>;
type PAGViewInstance = Awaited<ReturnType<PAGModule['PAGView']['init']>>;
type PAGFileInstance = Awaited<ReturnType<PAGModule['PAGFile']['load']>>;
type SoftwareDecoderFactory = Exclude<Parameters<PAGModule['registerSoftwareDecoderFactory']>[0], null>;
type FFAVCInitOptions = { locateFile?: (file: string) => string; };
type PAGModuleWithHeap = PAGModule & {
  HEAP8?: Int8Array;
  HEAPU8?: Uint8Array;
};

declare global {
  interface Window {
    ffavc?: {
      FFAVCInit: (options?: FFAVCInitOptions) => Promise<{
        FFAVCDecoderFactory: new () => SoftwareDecoderFactory;
      }>;
    };
  }
}

// 模块级单例，保证 libpag wasm 只懒加载并初始化一次，后续弹窗复用。
let pagModulePromise: Promise<PAGModule> | null = null;
let firefoxSoftwareDecoderPromise: Promise<void> | null = null;

/**
 * 判断当前浏览器是否为 Firefox；Firefox 播放含 BMP/video sequence 的 PAG 需要软件解码兜底。
 */
function isFirefoxBrowser() {
  return typeof navigator !== 'undefined' && /Firefox/i.test(navigator.userAgent);
}

/**
 * 动态加载外部脚本；已存在同 src 脚本时复用，避免重复插入。
 */
function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * ffavc 渲染帧时读取 pag.HEAP8；libpag ESM 仅暴露 HEAPU8，需要补一个同 buffer 的 HEAP8 视图。
 */
function ensurePagHeap8ForSoftwareDecoder(PAG: PAGModule) {
  const pagWithHeap = PAG as PAGModuleWithHeap;
  if (pagWithHeap.HEAP8 || !pagWithHeap.HEAPU8) {
    return;
  }

  Object.defineProperty(pagWithHeap, 'HEAP8', {
    configurable: true,
    enumerable: false,
    get() {
      return new Int8Array(pagWithHeap.HEAPU8!.buffer);
    },
  });
}

/**
 * Firefox 下注册 ffavc 软件解码器，匹配 PAG 官方预览页的兼容策略。
 */
function ensureFirefoxSoftwareDecoder(PAG: PAGModule) {
  if (!isFirefoxBrowser()) {
    return Promise.resolve();
  }

  if (!firefoxSoftwareDecoderPromise) {
    firefoxSoftwareDecoderPromise = loadScript(FFAVC_SCRIPT_URL)
      .then(async () => {
        const FFAVC = await window.ffavc?.FFAVCInit({
          locateFile: () => FFAVC_WASM_URL,
        });
        if (!FFAVC) {
          throw new Error('FFAVC init failed');
        }
        ensurePagHeap8ForSoftwareDecoder(PAG);
        const ffavcDecoderFactory = new FFAVC.FFAVCDecoderFactory();
        PAG.registerSoftwareDecoderFactory(ffavcDecoderFactory);
      })
      .catch((error) => {
        firefoxSoftwareDecoderPromise = null;
        console.warn('[PagPlayer] Firefox software decoder init failed.', error);
      });
  }

  return firefoxSoftwareDecoderPromise;
}

/**
 * 懒加载并初始化 libpag WebAssembly 模块，全局仅执行一次；失败时重置以便后续重试。
 */
function loadPagModule(): Promise<PAGModule> {
  if (!pagModulePromise) {
    pagModulePromise = import('libpag')
      .then(({ PAGInit }) => PAGInit({ locateFile: () => PAG_WASM_URL }))
      .catch((error) => {
        pagModulePromise = null;
        throw error;
      });
  }
  return pagModulePromise;
}

interface PagPlayerProps {
  /** PAG 文件地址。 */
  url: string;
  /** 附加到 canvas 上的类名。 */
  className?: string;
  /** 是否循环播放，默认循环。 */
  loop?: boolean;
  /** PAGView 重复播放次数；undefined 时沿用 loop 的无限 / 单次逻辑。 */
  repeatCount?: number;
  /** canvas 点击回调。 */
  onClick?: () => void;
  /** 加载或初始化失败回调，由上层决定兜底策略。 */
  onError?: (error: unknown) => void;
  /** 首帧就绪并开始播放回调。 */
  onReady?: () => void;
}

/**
 * PAG 动画播放组件，收敛 libpag 的懒加载、初始化、播放与销毁全部逻辑。
 * 仅在客户端运行；加载或初始化失败时通过 onError 上抛，自身不渲染兜底 UI。
 */
export default function PagPlayer({
  url,
  className,
  loop = true,
  repeatCount,
  onClick,
  onError,
  onReady,
}: PagPlayerProps) {
  const isClient = useIsClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);

  // 保持回调引用最新，避免因父组件重建函数导致播放 effect 反复重启。
  onErrorRef.current = onError;
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!isClient || !url) return undefined;

    let disposed = false;
    const abortController = new AbortController();
    let pagView: PAGViewInstance;
    let pagFile: PAGFileInstance;

    const start = async () => {
      try {
        const [PAG, buffer] = await Promise.all([
          loadPagModule(),
          fetch(url, { signal: abortController.signal }).then((res) => {
            if (!res.ok) {
              throw new Error(`PAG resource request failed: ${res.status}`);
            }
            return res.arrayBuffer();
          }),
        ]);
        if (disposed || !canvasRef.current) return;

        await ensureFirefoxSoftwareDecoder(PAG);
        if (disposed || !canvasRef.current) return;

        pagFile = await PAG.PAGFile.load(buffer);
        if (disposed || !canvasRef.current) return;

        const canvas = canvasRef.current;
        canvas.width = pagFile.width();
        canvas.height = pagFile.height();

        pagView = await PAG.PAGView.init(pagFile, canvas, {
          firstFrame: !isFirefoxBrowser(),
        });
        if (disposed || !pagView) {
          pagView?.destroy();
          return;
        }

        pagView.setRepeatCount(repeatCount ?? (loop ? 0 : 1));
        await pagView.play();
        onReadyRef.current?.();
      } catch (error) {
        if (disposed || abortController.signal.aborted) return;
        onErrorRef.current?.(error);
      }
    };

    start();

    return () => {
      disposed = true;
      abortController.abort();
      pagView?.destroy();
      pagFile?.destroy();
    };
  }, [isClient, url, loop, repeatCount]);

  if (!isClient) return null;

  return (
    <canvas
      ref={canvasRef}
      className={classNames(styles.pagCanvas, className)}
      onClick={onClick}
    />
  );
}
